import './Document.scss';
import classNames from 'classnames';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { limitString } from 'components/Pipes/textPipes';
import { Svgicon } from 'components/Svgicon/Svgicon';
import { type SelectOption } from 'hooks';
import {
  type FC,
  type SyntheticEvent,
} from 'react';
import { isMobile, isTablet } from 'services/mobile';
import {
  type DocumentDTO,
  getItemsFromStorage,
  type LoginAsFromStorage} from 'types';
import { COLORS } from 'types/Colors';

type DocumentComponentProps = DocumentDTO & {
  displayType: SelectOption,
  documentGroupName: string,
  documentOnClick:
  (event: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLElement>)
  => Promise<void>,
  downloadClick:
  (event: SyntheticEvent | undefined) => Promise<void>,
  fileByIdLoading: boolean,
  period: number | string,
  toggleDocumentViewedStatus:
  (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => Promise<void>,
  viewingFileByIdLoading: boolean,
};

export const UsualDocument: FC<DocumentComponentProps> = ({
  accountName,
  displayType,
  documentGroupName,
  documentOnClick,
  downloadClick,
  fileByIdLoading,
  fileType,
  fundName,
  period,
  toggleDocumentViewedStatus,
  userViewed,
  version,
  viewingFileByIdLoading,
}) => {
  const mobile = isMobile();
  const {loginAs} = getItemsFromStorage<LoginAsFromStorage>([
    'loginAs',
  ], sessionStorage);

  const documentLeftSide = (type: string) => <Svgicon
    className='relative size-6'
    dataTest={userViewed ? 'document-viewed' : 'document-new'}
    id={userViewed ? 'seen-recent-document' : 'new-recent-document'}
    text={type.slice(0, 3)}
  />;

  const buttonsSide = () =>
    <div className='show-on-hover'>
      {!userViewed && !loginAs && <Button
        data-test='mark-file-as-seen'
        icon='only'
        iconColor={COLORS['custom-blue']}
        iconHoveredColor={COLORS['$main-500']}
        iconid='mark-seen'
        isLoading={viewingFileByIdLoading}
        onClick={(event) => toggleDocumentViewedStatus(event)}
        size='medium'
        tooltip='Mark as seen'
        type='transparent'
      > </Button>}
      <Button
        data-test='download-file'
        icon='only'
        iconColor={COLORS['custom-blue']}
        iconHoveredColor={COLORS['$main-500']}
        iconid='download-arrow-1f'
        isLoading={fileByIdLoading}
        onClick={(event) => downloadClick(event)}
        size='medium'
        tooltip='Download'
        type='transparent'
      />
    </div>;

  const mainName = displayType.id === 'type' ? `${version ? `#${version}` : ''} ${period}` : `${documentGroupName}`;

  const rightContentForOtherDoc = () =>
    <div className='doc-right flex w-full flex-row items-center justify-between overflow-visible'>
      <div className='doc-left-of-right flex flex-col items-start overflow-hidden'>
        <div className='flex gap-[0.25rem]'>
          <p className={`${userViewed && '!font-normal'} mb-0 whitespace-nowrap text-font-2 font-semibold text-black-999`}>
            {mainName}
          </p>
          {isTablet() ? <p className={classNames({'!font-normal': userViewed}, 'mb-0 self-center whitespace-nowrap text-font-2 font-medium text-black-999')}>
            {accountName === 'Entire fund' ? '' : ` • ${limitString(accountName, 25)}`}
          </p> : null}
        </div>
        <div className='flex items-center gap-[0.25rem] text-black-500'>
          <Svgicon className='size-[0.625rem]' id='luggage-04' />
          <div className='max-w-52'>
            <p className='acc-or-fund-name m-0 truncate text-font-4 font-medium '>
              {fundName}
            </p>
          </div>
        </div>
      </div>
      <div className='flex max-w-[5rem] flex-row items-center justify-between gap-[0.375rem] ms:min-w-max ms:max-w-full'>
        {isTablet() ? null : <span className='truncate  text-font-4 font-medium text-black-500'>
          {accountName === 'Entire fund' ? '' : accountName}
        </span>}
        {!mobile && buttonsSide()}
      </div>
    </div>;

  return (
    <div
      className='documents-container flex cursor-pointer flex-row items-center gap-[0.625rem] overflow-visible !rounded-DEFAULT border-b-black-100 px-[0.5rem] py-[0.25rem]'
      data-test={`document-${period}`}
      onClick={(event) => documentOnClick(event)}
      role='button'
      tabIndex={0}
    >
      <div className='flex flex-row items-center'>
        {documentLeftSide(fileType)}
      </div>
      {rightContentForOtherDoc()}
    </div>
  );
};
